import type { ComponentType, ReactNode } from 'react';
import { usePageContext } from '../../usePageContext.ts';
import { GAINS_GROUPS, TOP_RANKINGS } from '../overview-options.ts';
import { OVERVIEW_TILES } from '../overview-tiles.ts';
import { ChoiceField } from './ChoiceField.tsx';
import {
  BREAKDOWN_DIMENSIONS,
  BREAKDOWN_MEASURES,
  chartsForMeasure,
  TOP_PRODUCT_CHARTS,
  TREND_CHARTS,
  TREND_MEASURES,
  withMeasure,
} from './widget-options.ts';
import type { WidgetKind, WidgetSettings } from './widget-types.ts';

export type SettingsFieldsProps<K extends WidgetKind> = {
  settings: WidgetSettings<K>;
  onChange: (settings: WidgetSettings<K>) => void;
};

type ChoiceKey<S> = { [F in keyof S]: S[F] extends string ? F : never }[keyof S];

type SettingChoiceProps<S, F extends ChoiceKey<S>> = {
  settings: S;
  field: F;
  label: string;
  choices: readonly (S[F] & string)[];
  labelOf: (value: S[F] & string) => string;
  onChange: (settings: S) => void;
  changed?: (settings: S, value: S[F] & string) => S;
};

const SettingChoice = <S, F extends ChoiceKey<S>>({
  settings,
  field,
  label,
  choices,
  labelOf,
  onChange,
  changed = (current, value) => ({ ...current, [field]: value }),
}: SettingChoiceProps<S, F>) => (
  <ChoiceField
    label={label}
    value={settings[field] as S[F] & string}
    choices={choices}
    labelOf={labelOf}
    onChange={(value) => {
      onChange(changed(settings, value));
    }}
  />
);

const useLabels = () => {
  const { t } = usePageContext();
  return {
    t,
    chart: (chart: string) => t(`dashboard.widgets.chart.${chart}`),
    group: (group: string) => t(`dashboard.overview.group.${group}`),
    chartLabel: t('dashboard.widgets.field.chart'),
    groupLabel: t('dashboard.overview.groupLabel'),
    measureLabel: t('dashboard.widgets.field.measure'),
  };
};

type KpiTile = WidgetSettings<'kpi'>['tile'];

const KPI_TILES = OVERVIEW_TILES.map((tile) => tile.key as KpiTile);

const KpiFields = ({ settings, onChange }: SettingsFieldsProps<'kpi'>) => {
  const { t } = useLabels();
  return (
    <SettingChoice
      settings={settings}
      field="tile"
      label={t('dashboard.widgets.field.tile')}
      choices={KPI_TILES}
      labelOf={(tile) => t(`dashboard.overview.${tile}`)}
      onChange={onChange}
    />
  );
};

const TrendFields = ({ settings, onChange }: SettingsFieldsProps<'trend'>) => {
  const labels = useLabels();
  return (
    <>
      <SettingChoice
        settings={settings}
        field="measure"
        label={labels.measureLabel}
        choices={TREND_MEASURES}
        labelOf={(measure) => labels.t(`dashboard.widgets.trendMeasure.${measure}`)}
        onChange={onChange}
      />
      <SettingChoice
        settings={settings}
        field="chart"
        label={labels.chartLabel}
        choices={TREND_CHARTS}
        labelOf={labels.chart}
        onChange={onChange}
      />
      <SettingChoice
        settings={settings}
        field="group"
        label={labels.groupLabel}
        choices={GAINS_GROUPS}
        labelOf={labels.group}
        onChange={onChange}
      />
    </>
  );
};

const BreakdownFields = ({ settings, onChange }: SettingsFieldsProps<'breakdown'>) => {
  const labels = useLabels();
  return (
    <>
      <SettingChoice
        settings={settings}
        field="dimension"
        label={labels.t('dashboard.widgets.field.dimension')}
        choices={BREAKDOWN_DIMENSIONS}
        labelOf={(dimension) => labels.t(`dashboard.widgets.dimension.${dimension}`)}
        onChange={onChange}
      />
      <SettingChoice
        settings={settings}
        field="measure"
        label={labels.measureLabel}
        choices={BREAKDOWN_MEASURES}
        labelOf={(measure) => labels.t(`dashboard.widgets.breakdownMeasure.${measure}`)}
        onChange={onChange}
        changed={withMeasure}
      />
      <SettingChoice
        settings={settings}
        field="chart"
        label={labels.chartLabel}
        choices={chartsForMeasure(settings.measure)}
        labelOf={labels.chart}
        onChange={onChange}
      />
      <SettingChoice
        settings={settings}
        field="group"
        label={labels.groupLabel}
        choices={GAINS_GROUPS}
        labelOf={labels.group}
        onChange={onChange}
      />
    </>
  );
};

type TopSize = WidgetSettings<'topProducts'>['size'];

const TOP_SIZE_CHOICES = ['5', '10', '20'] as const;

const TopProductsFields = ({ settings, onChange }: SettingsFieldsProps<'topProducts'>) => {
  const labels = useLabels();
  return (
    <>
      <SettingChoice
        settings={settings}
        field="rankedBy"
        label={labels.t('dashboard.overview.rankLabel')}
        choices={TOP_RANKINGS}
        labelOf={(ranking) => labels.t(`dashboard.overview.rank.${ranking}`)}
        onChange={onChange}
      />
      <ChoiceField
        label={labels.t('dashboard.overview.sizeLabel')}
        value={String(settings.size) as (typeof TOP_SIZE_CHOICES)[number]}
        choices={TOP_SIZE_CHOICES}
        labelOf={(size) => labels.t('dashboard.overview.size', { count: Number(size) })}
        onChange={(size) => {
          onChange({ ...settings, size: Number(size) as TopSize });
        }}
      />
      <SettingChoice
        settings={settings}
        field="chart"
        label={labels.chartLabel}
        choices={TOP_PRODUCT_CHARTS}
        labelOf={labels.chart}
        onChange={onChange}
      />
    </>
  );
};

const NoFields = (): ReactNode => null;

export const SETTINGS_FIELDS: { readonly [K in WidgetKind]: ComponentType<SettingsFieldsProps<K>> } = {
  kpi: KpiFields,
  trend: TrendFields,
  breakdown: BreakdownFields,
  funnel: NoFields,
  topProducts: TopProductsFields,
};
